import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalytics } from '~/analytics';
import { fetchWithSessionRefresh } from '~/auth/sessionFetch';
import { useOtto } from '~/components/otto/OttoProvider';
import { type PageContext, toChatPageContext } from '~/hooks/usePageContext';
import { apiHost, getAccessToken } from '~/utils/api';

const CHAT_API = `${apiHost}/api/v1/genui/chat`;

// Headless per-session chat host. Owns the `useChat` instance and forwards
// messages/status into the provider. Mounted independently of the panel so
// streams keep running when the panel closes or the user switches sessions.
//
// Per-session instrumentation: ttft, completion, tool-invocation, errors,
// and aborts all fire from this runner against the session's own state.
// With concurrent runners the events match server-observed turns exactly,
// which is what the AI-reliability dashboard joins against the backend
// `genui.chat.turn` metric.
//
// `experimental_throttle` coalesces the many rapid message updates that
// arrive during tool streaming — without it, React 19 can hit the
// "Maximum update depth exceeded" guard when long tool outputs land.
const STREAM_THROTTLE_MS = 80;

function countUserMessages(messages: UIMessage[]): number {
  let n = 0;
  for (const m of messages) if (m.role === 'user') n++;
  return n;
}

function parseErrorCode(message: string): string {
  // Mirrors the chat_error_displayed contract: only the bounded code
  // ships to analytics. Full text/stack live in Sentry. The wire format
  // is `{"code": "...", "message": "..."}` JSON; on parse failure we
  // fall back to 'unknown'.
  try {
    const parsed: unknown = JSON.parse(message);
    if (parsed && typeof parsed === 'object') {
      const code = (parsed as Record<string, unknown>).code;
      if (typeof code === 'string' && code.trim() !== '') return code;
    }
  } catch {
    /* not JSON */
  }
  return 'unknown';
}

export function OttoChatRunner({
  sessionId,
  initialMessages,
  pageContextRef,
}: {
  sessionId: string;
  initialMessages: UIMessage[];
  pageContextRef?: RefObject<PageContext | null>;
}) {
  const { onMessagesChange, onStatusChange, registerConversation } = useOtto();
  const { track } = useAnalytics();

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: CHAT_API,
        headers: async () => {
          const token = await getAccessToken();
          return { Authorization: `Bearer ${token}` };
        },
        credentials: 'omit',
        body: () => ({ pageContext: toChatPageContext(pageContextRef?.current ?? null) }),
        // A lapsed session is refreshed and the request retried once, as the SDK does.
        fetch: fetchWithSessionRefresh,
      }),
    [pageContextRef],
  );

  // useChat only reads `messages` at construction time (and when `id`
  // changes), so passing the prop on every render is safe — the Chat
  // instance is preserved as long as `sessionId` doesn't change.
  const { messages, sendMessage, stop, status, error } = useChat({
    id: sessionId,
    transport,
    messages: initialMessages,
    experimental_throttle: STREAM_THROTTLE_MS,
  });

  // ── Instrumentation refs ────────────────────────────────────────────
  // Source of truth for the current turn's timing. Cleared when the
  // turn completes, errors, or aborts — so the unmount-abort path can
  // tell "still streaming" from "settled cleanly" by checking nullness.
  const turnStartMsRef = useRef<number | null>(null);
  // Tracks the previous status so the effect can spot transitions
  // (e.g. submitted → streaming fires ttft; streaming → ready fires
  // completion). Seeded with the initial status so the very first
  // render doesn't synthesise a spurious transition.
  const prevStatusRef = useRef(status);
  // Has ttft fired for the current turn? Reset by the user-message
  // detector below when the turn count goes up.
  const firstTokenLoggedRef = useRef(false);
  // Per-turn user-message counter. Pre-seeded from initialMessages so a
  // resumed session continues from the right turn index instead of
  // re-numbering from zero. Each newly-arrived user message bumps this
  // and resets per-turn state.
  const turnIndexRef = useRef(countUserMessages(initialMessages) - 1);
  const lastUserCountRef = useRef(countUserMessages(initialMessages));
  // Dedupe sets for tool events — toolCallId is unique per call, so
  // remembering the IDs we've already emitted prevents the message-walk
  // effect from re-firing chat_tool_invoked / chat_tool_result_rendered
  // on every streamed token. Pre-seeded from initialMessages so resumed
  // sessions don't replay historical tool calls into the funnel.
  const seenToolIdsRef = useRef<Set<string>>(new Set());
  const seenToolOutcomesRef = useRef<Set<string>>(new Set());
  // Last error text we emitted for, so a transient error->error change
  // (same content, new object reference) doesn't fire repeatedly.
  const lastErrorRef = useRef<string | null>(null);
  // Set by the user's explicit Stop click before the handle's stop()
  // resolves. The unmount-abort effect checks this to decide which
  // `reason` to use (or to skip the unmount fire when the stop button
  // already emitted one).
  const stoppedExplicitlyRef = useRef(false);

  // Pre-seed the tool dedupe sets from any historical tool parts in
  // `initialMessages`. This is the resume path: without seeding, the
  // message-walk effect treats every prior tool call as a fresh
  // invocation and floods the funnel.
  useEffect(() => {
    for (const msg of initialMessages) {
      if (msg.role !== 'assistant') continue;
      for (const part of msg.parts) {
        if (!part.type.startsWith('tool-')) continue;
        const tp = part as { toolCallId?: string; state?: string };
        if (!tp.toolCallId) continue;
        seenToolIdsRef.current.add(tp.toolCallId);
        if (tp.state === 'output-available' || tp.state === 'output-error') {
          seenToolOutcomesRef.current.add(`${tp.toolCallId}:${tp.state}`);
        }
      }
    }
    // initialMessages is constant for the lifetime of the runner
    // (sessionId-keyed mount), so this only runs once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect new user-message arrivals to bump the turn counter and reset
  // per-turn state. Runs off `messages` so streamed assistant content
  // doesn't disturb the counter — only a fresh user message bumps it.
  useEffect(() => {
    const userCount = countUserMessages(messages);
    if (userCount > lastUserCountRef.current) {
      lastUserCountRef.current = userCount;
      turnIndexRef.current = userCount - 1;
      turnStartMsRef.current = performance.now();
      firstTokenLoggedRef.current = false;
    }
  }, [messages]);

  // ttft + completion off status transitions.
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev === status) return;
    const turnStart = turnStartMsRef.current;
    const turnIndex = turnIndexRef.current;
    if (turnIndex < 0) return;

    if (status === 'streaming' && !firstTokenLoggedRef.current && turnStart != null) {
      firstTokenLoggedRef.current = true;
      track('chat_first_token_received', {
        session_id: sessionId,
        turn_index: turnIndex,
        ttft_ms: Math.round(performance.now() - turnStart),
      });
    }

    if (prev === 'streaming' && status === 'ready' && turnStart != null) {
      const last = messages[messages.length - 1];
      const runIdPart = last?.parts.find((p) => (p as { type?: string }).type === 'data-run-id') as
        | { data?: { runId?: string } }
        | undefined;
      // Count distinct tool-call IDs in the just-finished assistant
      // message. Using a Set guards against the same tool part
      // appearing across multiple stream chunks.
      const toolCountThisTurn = last
        ? last.parts
            .filter((p) => p.type.startsWith('tool-') && 'toolCallId' in p)
            .reduce((acc, p) => {
              const id = (p as { toolCallId?: string }).toolCallId;
              return id ? acc.add(id) : acc;
            }, new Set<string>()).size
        : 0;
      track('chat_message_completed', {
        session_id: sessionId,
        turn_index: turnIndex,
        duration_ms: Math.round(performance.now() - turnStart),
        tool_count: toolCountThisTurn,
        run_id: runIdPart?.data?.runId ?? null,
      });
      turnStartMsRef.current = null;
    }
  }, [status, messages, sessionId, track]);

  // Tool invocation + outcome events. Walks only the last assistant
  // message — historical messages can't grow new tool parts during this
  // turn, so iterating the full history on every streamed token (which
  // can be hundreds per turn) is wasted work. Resume safety: the
  // initial-message seed above ensures historical tool calls don't
  // re-emit on the first render after mount.
  useEffect(() => {
    let lastAssistant: UIMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        lastAssistant = messages[i];
        break;
      }
    }
    if (!lastAssistant) return;
    const turnIndex = turnIndexRef.current;
    if (turnIndex < 0) return;
    for (const part of lastAssistant.parts) {
      if (!part.type.startsWith('tool-')) continue;
      const tp = part as { type: string; toolCallId?: string; state?: string };
      const toolId = tp.toolCallId;
      if (!toolId) continue;
      const toolName = tp.type.replace(/^tool-/, '');
      if (!seenToolIdsRef.current.has(toolId)) {
        seenToolIdsRef.current.add(toolId);
        track('chat_tool_invoked', {
          session_id: sessionId,
          turn_index: turnIndex,
          tool_name: toolName,
        });
      }
      if (tp.state === 'output-available' || tp.state === 'output-error') {
        const outcomeKey = `${toolId}:${tp.state}`;
        if (!seenToolOutcomesRef.current.has(outcomeKey)) {
          seenToolOutcomesRef.current.add(outcomeKey);
          track('chat_tool_result_rendered', {
            session_id: sessionId,
            turn_index: turnIndex,
            tool_name: toolName,
            result_status: tp.state === 'output-available' ? 'success' : 'error',
          });
        }
      }
    }
  }, [messages, sessionId, track]);

  // Error events. Dedupes on `error.message` so re-renders that bring
  // the same error object through don't re-fire. Cleared when error
  // resolves back to undefined.
  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null;
      return;
    }
    if (lastErrorRef.current === error.message) return;
    lastErrorRef.current = error.message;
    track('chat_error_displayed', {
      session_id: sessionId,
      turn_index: Math.max(0, turnIndexRef.current),
      error_code: parseErrorCode(error.message),
    });
  }, [error, sessionId, track]);

  // Stop button: fires reason='stop_button' inline before invoking the
  // underlying useChat stop. Sets a sentinel so the unmount-abort effect
  // doesn't double-fire if the runner subsequently unmounts.
  const handleStop = useCallback(() => {
    const turnStart = turnStartMsRef.current;
    const turnIndex = turnIndexRef.current;
    if (turnStart != null && turnIndex >= 0) {
      track('chat_stream_aborted', {
        session_id: sessionId,
        turn_index: turnIndex,
        elapsed_ms: Math.round(performance.now() - turnStart),
        reason: 'stop_button',
      });
      stoppedExplicitlyRef.current = true;
      turnStartMsRef.current = null;
    }
    void stop();
  }, [stop, sessionId, track]);

  useEffect(() => {
    const unregister = registerConversation(sessionId, {
      send: (text: string) => void sendMessage({ text }),
      stop: handleStop,
    });
    return unregister;
  }, [sessionId, sendMessage, handleStop, registerConversation]);

  useEffect(() => {
    onMessagesChange(sessionId, messages);
  }, [sessionId, messages, onMessagesChange]);

  useEffect(() => {
    onStatusChange(sessionId, status, error?.message ?? null);
  }, [sessionId, status, error, onStatusChange]);

  // Best-effort unmount-abort. Fires only if the runner is going away
  // mid-stream AND the user didn't already click Stop (which clears
  // turnStartMsRef). Browser-close still won't reliably trigger this —
  // the server-side `genui.chat.turn` metric is the source of truth for
  // forensic abort accounting.
  useEffect(() => {
    return () => {
      if (stoppedExplicitlyRef.current) return;
      const status = prevStatusRef.current;
      if (status !== 'streaming' && status !== 'submitted') return;
      const turnStart = turnStartMsRef.current;
      const turnIndex = turnIndexRef.current;
      if (turnStart == null || turnIndex < 0) return;
      track('chat_stream_aborted', {
        session_id: sessionId,
        turn_index: turnIndex,
        elapsed_ms: Math.round(performance.now() - turnStart),
        reason: 'unmount',
      });
    };
    // Unmount-only emit — intentionally empty deps so this never re-runs
    // mid-life; the refs read inside are always current at cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
